import {
  Autocomplete,
  Stack,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { IncidenciasTasaRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";
import { useCentros, type Centro } from "../../../hooks/useCentros";
import { useProfesores, type Profesor } from "../../../hooks/useProfesores";
import { useIncidenciasTasaReport } from "../../../hooks/useReportJob";

export default function IncidenciasTasaPage() {
  const { t } = useTranslation<"translation">();
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));
  const [profSel, setProfSel] = useState<Profesor | null>(null);
  const [centroSel, setCentroSel] = useState<Centro | null>(null);

  const { rows: profesores } = useProfesores();
  const { rows: centros } = useCentros();

  const { run, useStatus } = useIncidenciasTasaReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const payload = {
      desde,
      hasta,
      profesorId: profSel?.id,
      centroId: centroSel?.id,
    };
    const { jobId } = await run(payload);
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const res = status?.result;

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("incidenciasTasa.titulo")}</Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          label={t("incidenciasTasa.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={t("incidenciasTasa.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Autocomplete
          options={profesores}
          getOptionLabel={(o) => o.nombre}
          value={profSel}
          onChange={(_, v) => setProfSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("filters.profesor")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Autocomplete
          options={centros.filter((c) => c.nombre !== "OTHER")}
          getOptionLabel={(o) => o.nombre}
          value={centroSel}
          onChange={(_, v) => setCentroSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("filters.centro")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("incidenciasTasa.filtros.generar")}
        </Button>
      </Stack>

      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{t("incidenciasTasa.estado.generando")}</Typography>
        </Stack>
      )}

      {status?.done && res?.url && (
        <Alert severity="success" sx={{ width: "fit-content" }}>
          XLSX:&nbsp;
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
          >
            {t("incidenciasTasa.estado.descargar")}
          </Button>
        </Alert>
      )}

      {status?.done && res && (
        <Stack spacing={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("incidenciasTasa.tablaProfesores.titulo")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("incidenciasTasa.tabla.nombre")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.incidencias")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.total")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.tasa")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {res.porProfesor.map((r: IncidenciasTasaRow, idx: number) => (
                  <TableRow key={`prof-${r.id ?? idx}`}>
                    <TableCell>{r.nombre}</TableCell>
                    <TableCell>{r.incidencias}</TableCell>
                    <TableCell>{r.total}</TableCell>
                    <TableCell>{r.tasa.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Divider />

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("incidenciasTasa.tablaCentros.titulo")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("incidenciasTasa.tabla.nombre")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.incidencias")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.total")}</TableCell>
                  <TableCell>{t("incidenciasTasa.tabla.tasa")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {res.porCentro.map((r: IncidenciasTasaRow, idx: number) => (
                  <TableRow key={`cent-${r.id ?? idx}`}>
                    <TableCell>{r.nombre}</TableCell>
                    <TableCell>{r.incidencias}</TableCell>
                    <TableCell>{r.total}</TableCell>
                    <TableCell>{r.tasa.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
